/**
 * This controller manages the customer permissions information.
 * Based on Flutter PermissionCustomerModelController
 * @author Based on Flutter implementation by Ali Nour Eldin
 */
class PermissionCustomerModelController {
  constructor(user = null) {
    this.permissionsList = [];
    
    // Check for PERMISSIONS (uppercase) or permissions (lowercase) for compatibility
    const permissionsData = user?.PERMISSIONS || user?.permissions;
    
    if (permissionsData) {
      try {
        let permissions;
        
        // Handle both string (JSON) and object formats
        if (typeof permissionsData === 'string') {
          permissions = JSON.parse(permissionsData);
        } else {
          permissions = permissionsData;
        }
        
        if (permissions && permissions.CUSTOMER !== undefined) {
          // Handle null, empty string, or empty array
          if (permissions.CUSTOMER === null || permissions.CUSTOMER === '' || !Array.isArray(permissions.CUSTOMER)) {
            this.permissionsList = [];
          } else {
            this.permissionsList = permissions.CUSTOMER || [];
          }
        } else {
          this.permissionsList = [];
        }
      } catch (error) {
        this.permissionsList = [];
      }
    }
  }

  static fromDevice(device = null) {
    const instance = new PermissionCustomerModelController();
    
    // Check for PERMISSION (uppercase, singular) or permission (lowercase) for compatibility
    const permissionData = device?.PERMISSION || device?.permission;
    
    if (permissionData) {
      try {
        let permissions;
        
        // Handle both string (JSON) and object formats
        if (typeof permissionData === 'string') {
          permissions = JSON.parse(permissionData);
        } else {
          permissions = permissionData;
        }
        
        if (permissions && permissions.CUSTOMER !== undefined) {
          // Handle null, empty string, or empty array
          if (permissions.CUSTOMER === null || permissions.CUSTOMER === '' || !Array.isArray(permissions.CUSTOMER)) {
            instance.permissionsList = [];
          } else {
            instance.permissionsList = permissions.CUSTOMER || [];
          }
        } else {
          instance.permissionsList = [];
        }
      } catch (error) {
        instance.permissionsList = [];
      }
    } else {
      instance.permissionsList = [];
    }
    
    return instance;
  }

  savePermission() {
    return JSON.stringify({ CUSTOMER: this.permissionsList });
  }

  getPermission() {
    return { CUSTOMER: this.permissionsList };
  }

  // Permission constants (static properties)
  static PER__CAN_DEPOSIT_DEMO = 'CAN_DEPOSIT_DEMO';
  static PER__CAN_DEPOSIT_BY_CARD = 'CAN_DEPOSIT_BY_CARD';
  static PER__CAN_CASH_OUT = 'CAN_CASH_OUT';
  static PER__CAN_CASH_IN = 'CAN_CASH_IN';
  static PER__CAN_EXCHANGE = 'CAN_EXCHANGE';
  static PER__CAN_INTERNAL_TRANSFER_MT5_USD = 'CAN_INTERNAL_TRANSFER_MT5_USD';
  static PER__CAN_INTERNAL_TRANSFER_MT5_MT5 = 'CAN_INTERNAL_TRANSFER_MT5_MT5';
  static PER__CAN_INTERNAL_TRANSFER_USD_MT5 = 'CAN_INTERNAL_TRANSFER_USD_MT5';
  static PER__MANAGEMENT_REAL_ACCOUNT = 'CAN_MANAGEMENT_REAL_ACCOUNT';
  static PER__ADD_REAL_ACCOUNT = 'CAN_ADD_REAL_ACCOUNT';
  static PER__MANAGEMENT_DEMO_ACCOUNT = 'CAN_MANAGEMENT_DEMO_ACCOUNT';
  static PER__ADD_DEMO_ACCOUNT = 'CAN_ADD_DEMO_ACCOUNT';
  static PER__CAN_ENABLE_TRADING_ACCOUNT__TRADING = 'CAN_ENABLE_TRADING_ACCOUNT__TRADING';
  static PER__CAN_ENABLE_TRADING_ACCOUNT__ACCOUNT = 'CAN_ENABLE_TRADING_ACCOUNT__ACCOUNT';
  static PER__SEND_MESSAGE = 'CAN_SEND_MESSAGE';
  static PER__CAN_UPDATE_LEVERAGE = 'CAN_UPDATE_LEVERAGE';
  static PER__CAN_VIEW_PENDING_REQUEST = 'CAN_VIEW_PENDING_REQUEST';
  static PER__CAN_VIEW_HISTORY = 'CAN_VIEW_HISTORY';
  static PER__CAN_VIEW_ACCOUNT_HISTORY = 'CAN_VIEW_ACCOUNT_HISTORY';
  static PER__CAN_VIEW_TOTAL_VOLUME_ACCOUNTS = 'CAN_VIEW_TOTAL_VOLUME_ACCOUNTS';
  static PER__CAN_DOWNLOAD_HISTORY_PDF = 'CAN_DOWNLOAD_HISTORY_PDF';
  // Checked on the account hides the feature. Unchecked leaves it visible.
  static PER__DISABLE_COPY_TRADE = 'DISABLE_COPY_TRADE';
  static PER__DISABLE_BECOME_MASTER = 'DISABLE_BECOME_MASTER';

  // Helper method to check if permission exists
  hasPermission(permission) {
    return this.permissionsList && this.permissionsList.includes(permission);
  }
}

export default PermissionCustomerModelController;
